import { useState, useEffect, useRef } from "react"
import { isEqual } from "lodash"
import WebViewer from "@pdftron/webviewer"

// apis
import {
  useGetDocAnnotations,
  usePostDocAnnotation,
  usePostBulkDocAnnotations,
  usePutDocAnnotation,
  useDeleteDocAnnotation,
} from "api/hooks/documents/useDocAnnotationsApi"
import { useUserInfo } from "api/hooks"
import { useGetEnvVars } from "api/hooks/enum"
import useAppState from "context/hooks/useAppState"

// styles
import "./PDFTronViewer.scss"

function findTagByName(xmlString, name) {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xmlString, "text/xml")
  const tags = xmlDoc.getElementsByTagName("*")

  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i]
    if (tag.getAttribute("name") === name) {
      return tag.outerHTML
    }
  }

  return null
}

function getAttributeValue(xmlString, attributeName) {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xmlString, "text/xml")
  const element = xmlDoc.documentElement

  if (element.hasAttribute(attributeName)) {
    return element.getAttribute(attributeName)
  }

  return null
}

function removeHiddenTags(xmlString) {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xmlString, "text/xml")

  const hiddenTags = xmlDoc.evaluate(
    "//*[@flags[contains(., 'hidden')]]",
    xmlDoc,
    null,
    XPathResult.UNORDERED_NODE_SNAPSHOT_TYPE,
    null
  )

  for (let i = 0; i < hiddenTags.snapshotLength; i++) {
    const hiddenTag = hiddenTags.snapshotItem(i)
    hiddenTag.parentNode.removeChild(hiddenTag)
  }

  const serializer = new XMLSerializer()
  const modifiedXmlString = serializer.serializeToString(xmlDoc)

  return modifiedXmlString
}

const PDFTronViewer = ({ metadata, file }) => {
  const { data: envVars } = useGetEnvVars()
  const { openToastNotification } = useAppState()
  const containerRef = useRef(null)
  const viewerRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)
  const setIsLoadingRef = useRef(setIsLoading)
  const { data: user } = useUserInfo()
  const postDocAnnotation = usePostDocAnnotation(metadata?.id)
  const postBulkDocAnnotation = usePostBulkDocAnnotations(metadata?.id)
  const putDocAnnotation = usePutDocAnnotation(metadata?.id)
  const deleteDocAnnotation = useDeleteDocAnnotation(metadata?.id)
  const {
    data: annotations,
    refetch: refetchAnnotations,
    isLoading: isAnnotLoading,
  } = useGetDocAnnotations(metadata?.id)
  const annotationsRef = useRef(annotations)

  const getExtensions = (file) => {
    if (typeof file === "string") {
      let segments = file.split("?AWSAccessKeyId=")
      let originalUrl = segments[0]
      let urlSeg = originalUrl.split(".")
      let extension = urlSeg[urlSeg.length - 1]
      return extension
    }

    return
  }

  const checkAnnotationUpdates = ({ newAnnotations, annot }) => {
    let canWrite = true
    let currentAnnotations = annotationsRef.current

    if (!isEqual(newAnnotations.annot_version_map, currentAnnotations.annot_version_map)) {
      const newAnnotSet = new Set(Object.keys(newAnnotations.annot_version_map))
      const currAnnotSet = new Set(Object.keys(currentAnnotations.annot_version_map))

      const notifNewAnnotIds = []
      const notifUpdatedAnnotIds = []
      const notifDeletedAnnotIds = []

      // check deleted annots
      Object.keys(currentAnnotations.annot_version_map).forEach((key) => {
        if (!newAnnotSet.has(key)) {
          notifDeletedAnnotIds.push(key)
        }
      })

      // check new or updated annots
      Object.keys(newAnnotations.annot_version_map).forEach((key) => {
        if (currAnnotSet.has(key)) {
          // check if existing annot has been updated
          if (newAnnotations.annot_version_map[key] !== currentAnnotations.annot_version_map[key]) {
            // annot has been modified
            notifUpdatedAnnotIds.push(key)
          }
        } else {
          // new annotation
          notifNewAnnotIds.push(key)
        }
      })

      notifNewAnnotIds.forEach((id) => {
        let target = findTagByName(newAnnotations.annotations_xfdf, id.toString())
        const annotAuthor = getAttributeValue(target, "title")
        let annotSubject = getAttributeValue(target, "subject")
        annotSubject = annotSubject === "Note" ? "Comment" : annotSubject
        openToastNotification(
          "info",
          "UPDATE",
          `${annotAuthor ? annotAuthor : "(User)"} added a new ${annotSubject.toLowerCase()}.`
        )
      })

      notifUpdatedAnnotIds.forEach((id) => {
        let target = findTagByName(newAnnotations.annotations_xfdf, id.toString())
        let annotAuthor = getAttributeValue(target, "title")
        let annotSubject = getAttributeValue(target, "subject")
        annotSubject = annotSubject === "UPDATE" ? "Comment" : annotSubject

        openToastNotification(
          "info",
          "Update",
          `${annotAuthor ? annotAuthor : "(User)"} updated their ${annotSubject}.`
        )
      })

      notifDeletedAnnotIds.forEach((id) => {
        let annotTag = findTagByName(currentAnnotations.annotations_xfdf, id)
        let inReplyToId = getAttributeValue(annotTag, "inreplyto")
        let parentTag = findTagByName(currentAnnotations.annotations_xfdf, inReplyToId)

        const annotPk = annot?.Pk?.toString()

        let annotSubject = getAttributeValue(annotTag, "subject")
        annotSubject = annotSubject === "Note" ? "Comment" : annotSubject

        if (id == annotPk) {
          // if user is trying to update a deleted annotation
          canWrite = false
          const annotAuthor = getAttributeValue(annotTag, "title")
          openToastNotification(
            "warning",
            "NOTE",
            `${annotSubject} deleted by "${annotAuthor ? annotAuthor : "(User)"}". Unable to update.`
          )
        } else {
          const annotAuthor = getAttributeValue(annotTag, "title")
          if (inReplyToId && parentTag) {
            openToastNotification(
              "warning",
              "NOTE",
              `The parent comment, to which ${annotAuthor} replied, has been deleted, along with ${annotAuthor}'s reply.`
            )
          } else {
            openToastNotification(
              "warning",
              "NOTE",
              `"${annotAuthor ? annotAuthor : "(User)"}" deleted their ${annotSubject.toLowerCase()}.`
            )
          }
        }
      })
    }

    annotationsRef.current = newAnnotations

    return canWrite
  }

  useEffect(() => {
    if (isAnnotLoading === false && user) {
      import("@pdftron/webviewer").then(() => {
        WebViewer(
          {
            path: "/lib",
            initialDoc: file,
            extension: getExtensions(file),
            licenseKey: envVars?.PDFTRON_LICENSE_KEY, // sign up to get a free trial key at https://dev.apryse.com
            annotationUser: user?.full_name,
          },
          containerRef.current
        ).then(async (instance) => {
          const { documentViewer, annotationManager } = instance.Core
          const { disableFeatures, openElements, Feature } = instance.UI

          instance.current = instance
          viewerRef.current = instance

          instance.UI.enableFeatures([instance.UI.Feature.InlineComment])
          instance.UI.disableElements(["multiSelectModeButton"])

          disableFeatures([Feature.Ribbons])
          openElements(["notesPanel"])

          const doAnnotsExistInDb = (xmlString) => {
            // Define a regular expression to match content between <add> or <modify> tags
            const regex = /<add>([\s\S]*?)<\/add>|<modify>([\s\S]*?)<\/modify>/

            // Test if the regex matches the provided XML string
            const matches = xmlString.match(regex)

            // Check if there is any non-whitespace content between <add> or <modify> tags
            return (
              (matches && matches[1] && matches[1].trim().length > 0) ||
              (matches && matches[2] && matches[2].trim().length > 0)
            )
          }

          const doAnnotsExistInPdf = (xmlString) => {
            const regex = /<annots>([\s\S]*?)<\/annots>/
            const match = regex.exec(xmlString)
            return match && match[1].trim().length > 0
          }

          // init load
          documentViewer.addEventListener("annotationsLoaded", async () => {
            setIsLoadingRef.current(false)
            annotationManager.exportAnnotations().then((xfdfData) => {
              let annotsExistInDb = doAnnotsExistInDb(annotations?.annotations_xfdf)
              let annotsExistOnPdf = doAnnotsExistInPdf(xfdfData)

              if (!annotsExistInDb && annotsExistOnPdf) {
                postBulkDocAnnotation({
                  body: {
                    annotations_xfdf: xfdfData,
                  },
                })
              }
            })

            const loadAnnotations = async (annotationManager, xfdf) => {
              const annotStrings = await annotationManager.importAnnotationCommand(xfdf)
              const pkSet = new Set()

              annotStrings.forEach((annot) => {
                pkSet.add(annot.Pk)
                if (annot) annotationManager.redrawAnnotation(annot)
              })
            }

            loadAnnotations(annotationManager, annotations?.annotations_xfdf)
            annotationsRef.current = annotations
          })

          // annotation updates
          annotationManager.addEventListener("annotationChanged", async (annots, action, { imported }) => {
            if (imported) return
            const xfdfString = await annotationManager.exportAnnotationCommand()
            const annot = annots[0]
            const annotation_id = annot?.Id

            refetchAnnotations().then(async (resp) => {
              if (resp.data) {
                let newAnnotations = resp.data
                let canWrite = checkAnnotationUpdates({ newAnnotations, annot })
                if (xfdfString) {
                }
                const formattedXfdf = removeHiddenTags(xfdfString)

                if (annot?.InReplyTo && !newAnnotations?.annot_version_map?.[annot.InReplyTo]) canWrite = false

                if (canWrite) {
                  if (action === "add") {
                    postDocAnnotation({
                      body: {
                        annotation_id,
                        annotation: formattedXfdf,
                      },
                    })
                  } else if (action === "modify") {
                    putDocAnnotation({
                      body: {
                        annotation_id,
                        version: annotationsRef.current?.annot_version_map?.[annotation_id],
                        annotation: formattedXfdf,
                      },
                    })
                  } else if (action === "delete" && annotationsRef.current?.annot_version_map?.[annotation_id]) {
                    deleteDocAnnotation({
                      body: {
                        annotation_id,
                        version: annotationsRef.current.annot_version_map[annotation_id],
                        annotation: formattedXfdf,
                      },
                    })
                  }
                } else {
                  refetchAnnotations()
                }
              }
            })
          })

          // select annotation listener
          annotationManager.addEventListener("annotationSelected", (annotations, action) => {
            if (action === "selected") {
              refetchAnnotations().then((resp) => {
                let newAnnotations = resp?.data
                checkAnnotationUpdates({ newAnnotations })
              })
            }
          })
        })
      })
    }
  }, [isAnnotLoading, user?.full_name])

  useEffect(() => {
    if (isLoading === false && viewerRef.current && file && isAnnotLoading === false && annotations) {
      const instance = viewerRef.current
      const { annotationManager } = instance.Core

      const reloadAnnots = async () => {
        annotationsRef.current = annotations
        const annotsXfdfList = await annotationManager.importAnnotationCommand(annotations.annotations_xfdf)
        await annotationManager.drawAnnotationsFromList(annotsXfdfList)

        const annots = annotationManager.getAnnotationsList()
        const hideAnnotations = annots.filter((a) => !annotations.annot_version_map[a.Pk])
        annotationManager.hideAnnotations(hideAnnotations)
      }
      reloadAnnots()
    }
  }, [JSON.stringify(annotations?.annot_version_map), isLoading, file, isAnnotLoading])

  return (
    <div
      className="pdftron-viewer-container"
      data-tour="pdftron-viewer"
    >
      <div
        className="webviewer"
        ref={containerRef}
      />
    </div>
  )
}

export default PDFTronViewer
